import React, { useState, useEffect, useRef } from 'react';
import usePersistentToggle from './usePersistentToggle';
import PogingBadge from './PogingBadge';

function KlinischRedenerenSection({ cases, answers, onAnswerChange, scores, onScoreChange, resetKey, onAnswerVersionsChange, kennisBoosterTitel, initialAnswerVersions }) {
  const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
  const [showModelAnswer, setShowModelAnswer] = useState(false);
  const [isAiChecking, setIsAiChecking] = useState(false);
  const [aiFeedback, setAiFeedback] = useState('');
  const [selfAssessment, setSelfAssessment] = useState({});
  const [showIntro, toggleIntro] = usePersistentToggle('klinisch_redeneren_intro', true);
  const [showTips, toggleTips] = usePersistentToggle('klinisch_redeneren_tips', false);
  const [answerVersions, setAnswerVersions] = useState(() => initialAnswerVersions || {});
  const [currentVersion, setCurrentVersion] = useState({});
  const [themeFilter, setThemeFilter] = useState('Alle Thema\'s');
  const [currentCases, setCurrentCases] = useState([...cases]);
  const [showCaseDescription, setShowCaseDescription] = useState(true);
  const [levelFilter, setLevelFilter] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAiFeedbackInfo, setShowAiFeedbackInfo] = useState(false);
  const aiFeedbackInfoRef = useRef(null);

  const levelOrder = ['beginner', 'gevorderd', 'expert'];
  const levelLabels = {
    beginner: 'Beginner',
    gevorderd: 'Gevorderd',
    expert: 'Expert'
  };

  useEffect(() => {
    let filteredCases = [...cases];
    if (themeFilter !== 'Alle Thema\'s') {
      filteredCases = cases.filter(c => c.theme === themeFilter);
    }
    if (statusFilter === 'answered') {
      filteredCases = filteredCases.filter(c => scores[c.id]);
    } else if (statusFilter === 'unanswered') {
      filteredCases = filteredCases.filter(c => !scores[c.id]);
    }
    setCurrentCases(filteredCases);
    setCurrentCaseIndex(0);
    setShowModelAnswer(false);
    setIsAiChecking(false);
    setAiFeedback('');
    setSelfAssessment({});
  }, [resetKey, cases, themeFilter, statusFilter]);

  // Bepaal huidige pogingnummer
  const savedVersionCount = answerVersions[currentCases[currentCaseIndex]?.id] ? (answerVersions[currentCases[currentCaseIndex].id] || []).length : 0;
  const attemptNumber = selfAssessment[currentCases[currentCaseIndex]?.id] ? savedVersionCount : savedVersionCount + 1;

  // Synchroniseer lokale selfAssessment met de doorgegeven scores zodat na herladen
  // de vergrendeling correct is.
  useEffect(() => {
    setSelfAssessment(scores);
  }, [scores]);

  useEffect(() => {
    // Alleen initialiseren vanuit props als lokale state nog leeg is
    if ((!answerVersions || Object.keys(answerVersions).length === 0) && initialAnswerVersions && Object.keys(initialAnswerVersions).length > 0) {
      setAnswerVersions(initialAnswerVersions);
    }
  }, [initialAnswerVersions]);

  const currentCase = currentCases[currentCaseIndex];

  const goToNextCase = () => {
    if (currentCaseIndex < currentCases.length - 1) {
      setCurrentCaseIndex(prev => prev + 1);
      setShowModelAnswer(false);
      setAiFeedback('');
    }
  };

  const goToPreviousCase = () => {
    if (currentCaseIndex > 0) {
      setCurrentCaseIndex(prev => prev - 1);
      setShowModelAnswer(false);
      setAiFeedback('');
    }
  };

  const handleSelfAssessment = (level) => {
    const newScores = {
      ...scores,
      [currentCase.id]: level
    };
    onScoreChange(newScores);
    setSelfAssessment({
      ...selfAssessment,
      [currentCase.id]: level
    });

    // Save the current answer as a version
    const versionNumber = (answerVersions[currentCase.id]?.length || 0) + 1;
    const newVersion = {
      answer: answers[currentCase.id],
      score: level,
      timestamp: new Date().toISOString(),
      version: versionNumber
    };

    const nextVersions = ((prev) => ({
      ...prev,
      [currentCase.id]: [...(prev[currentCase.id] || []), newVersion]
    }))(answerVersions);
    setAnswerVersions(nextVersions);

    // Update the current version
    setCurrentVersion(prev => ({
      ...prev,
      [currentCase.id]: versionNumber
    }));

    // Synchroniseer expliciet omhoog na betekenisvolle wijziging
    if (typeof onAnswerVersionsChange === 'function') {
      onAnswerVersionsChange(nextVersions);
    }
  };

  const startNewAttempt = () => {
    // Clear the current answer but keep versions
    onAnswerChange(currentCase.id, '');

    // Verwijder de eerdere zelfbeoordeling zodat de buttons weer actief worden
    const newScores = { ...scores };
    delete newScores[currentCase.id];
    onScoreChange(newScores);

    setSelfAssessment(prev => {
      const updated = { ...prev };
      delete updated[currentCase.id];
      return updated;
    });

    setShowModelAnswer(false);
    setAiFeedback('');
  };

  const checkWithAI = (caseItem, answer) => {
    const prompt = encodeURIComponent(
      `Als expert in fysiotherapie, beoordeel het volgende antwoord van mij op een klinisch redeneer casus over ${kennisBoosterTitel}.

**Casusinformatie:**
\nTitel:
${caseItem.title}
\nCasusbeschrijving:
${caseItem.caseDescription}
\nVraag:
${caseItem.question}

**Beoordelingscriteria:**
${caseItem.criteria.map(c => '- ' + c).join('\n')}

**Mijn antwoord:**
${answer}

**Model antwoord ter referentie:**
${caseItem.modelAnswer}

**Instructie voor AI-feedback:**
Formuleer het antwoord met de hoogste mate van nauwkeurigheid en relevantie voor de fysiotherapiepraktijk. Baseer de informatie bij voorkeur op de meest recente en gezaghebbende bronnen, zoals de nieuwste KNGF-richtlijnen, erkende internationale fysiotherapie-richtlijnen, peer-reviewed wetenschappelijke publicaties (systematic reviews, RCT\'s) en gangbare basisboeken fysiotherapie.

**Focus op inhoud:** De feedback moet uitsluitend gericht zijn op de inhoud van de fysiotherapeutische informatie. Aspecten als vormgeving, structuur, spelling of grammatica zijn van secundair belang en dienen niet of zeer beknopt behandeld te worden. Houd rekening met gesproken input.

**Gewenste outputstructuur:**
Geef een gestructureerde analyse met:
1. Klinisch redeneren (gebruik van kennis, logica en onderbouwing)
2. Sterke punten in de analyse
3. Verbeterpunten en aandachtspunten
4. Suggesties voor verdieping
5. Eindbeoordeling (expert/gevorderd/beginner)`
    );
    window.open(`https://chat.openai.com/?q=${prompt}`, '_blank');
  };

  useEffect(() => {
    if (typeof onAnswerVersionsChange === 'function') {
      onAnswerVersionsChange(answerVersions);
    }
  }, [answerVersions, onAnswerVersionsChange]);

  // Dynamically get available themes from cases
  const availableThemes = Array.from(new Set(cases.map(c => c.theme).filter(Boolean)));
  const filterOptions = ['Alle Thema\'s', ...availableThemes];

  useEffect(() => {
    if (!showAiFeedbackInfo) return;
    function handleClickOutside(event) {
      if (aiFeedbackInfoRef.current && !aiFeedbackInfoRef.current.contains(event.target) && !event.target.closest('button[aria-label="Feedback van AI"]')) {
        setShowAiFeedbackInfo(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAiFeedbackInfo]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-blue-700">Klinisch Redeneren</h2>
      
      <div className="mb-4">
        <button
          className="text-blue-700 font-semibold mb-2 focus:outline-none flex items-center gap-2"
          onClick={toggleIntro}
          aria-expanded={showIntro}
          aria-controls="klinischredeneren-intro"
        >
          {showIntro ? '▼' : '►'} Inleiding
        </button>
        {showIntro && (
          <div id="klinischredeneren-intro" className="bg-blue-50 p-4 rounded-lg shadow-sm">
            <p className="text-gray-700 leading-relaxed">
              Welkom bij Klinisch Redeneren, waar we de diepte ingaan! Hier leer je om je kennis toe te passen in complexe, realistische situaties. Je ontwikkelt het vermogen om klinische problemen te analyseren, verschillende opties te overwegen en weloverwogen beslissingen te nemen. Dit is waar je leert om theorie om te zetten in praktische vaardigheden.
            </p>
          </div>
        )}
      </div>

      <div className="mb-4">
        <button
          className="text-blue-700 font-semibold mb-2 focus:outline-none flex items-center gap-2"
          onClick={toggleTips}
          aria-expanded={showTips}
          aria-controls="klinischredeneren-tips"
        >
          {showTips ? '▼' : '►'} Bekijk tips voor effectief oefenen
        </button>
        {showTips && (
          <div id="klinischredeneren-tips" className="bg-blue-50 p-4 rounded-lg shadow-sm space-y-4">
            <h3 className="font-semibold text-blue-800 mb-2">Werkwijze</h3>
            <ol className="list-decimal ml-6 space-y-2 text-gray-700">
              <li>Lees de casusbeschrijving zorgvuldig door en identificeer de belangrijkste punten.</li>
              <li>Schrijf je analyse op in een logische volgorde, van observatie naar interpretatie.</li>
              <li>Gebruik je theoretische kennis om je redenering te onderbouwen.</li>
              <li>Vergelijk je analyse met het modelantwoord en/of gebruik de AI-feedback.</li>
              <li>Reflecteer op je eigen redenering: wat ging goed en wat kan beter?</li>
            </ol>

            <h3 className="font-semibold text-blue-800 mt-4 mb-2">Tip</h3>
            <p className="text-gray-700">
              Wil je liever spreken dan typen? Gebruik dan bijvoorbeeld:
            </p>
            <ul className="list-disc ml-6 text-gray-700">
              <li>De sneltoets van Windows: Windows-logotoets + H</li>
              <li>Op een Mac is dit meestal Fn (twee keer)</li>
              <li>Of spreek je analyse in bij ChatGPT (gebruik de microfoonfunctie en kopieer de tekst naar deze app).</li>
            </ul>
            <p className="text-gray-700 mt-2 mb-1">
              Je gesproken tekst wordt automatisch omgezet naar tekst.
            </p>
            <p className="text-gray-700 mt-0">
              <span className="font-semibold">Extra tip voor dicteren:</span> Voor de beste resultaten bij spraak-naar-tekst, zorg voor een rustige omgeving en gebruik een microfoon van goede kwaliteit. Duidelijk spreken helpt de software om je woorden nauwkeurig om te zetten!
            </p>
          </div>
        )}
      </div>

      <div className="mb-4 p-4 bg-gray-100 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Filters</h3>
        <div className="flex flex-wrap gap-2 items-start">
          {/* Thema */}
          <div className="w-full sm:w-auto">
            <div className="text-xs font-semibold text-gray-500 mb-1 pl-1">Thema</div>
            <div className="flex gap-2">
              {filterOptions.map(theme => {
                const casesForTheme = theme === 'Alle Thema\'s' ? cases : cases.filter(c => c.theme === theme);
                if (casesForTheme.length > 0) {
                  return (
                    <button
                      key={theme}
                      onClick={() => setThemeFilter(theme)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border border-gray-300
                        ${themeFilter === theme
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 hover:bg-blue-100'}
                      `}
                    >
                      {theme}
                    </button>
                  );
                }
                return null;
              })}
            </div>
          </div>
          {/* Casusstatus */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1 pl-1">Casusstatus</div>
            <div className="flex gap-2">
              <button onClick={() => setStatusFilter('all')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border border-gray-300 ${statusFilter === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-blue-100'}`}>Alle casussen</button>
              <button onClick={() => setStatusFilter('answered')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border border-gray-300 ${statusFilter === 'answered' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-blue-100'}`}>Beantwoorde casussen</button>
              <button onClick={() => setStatusFilter('unanswered')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border border-gray-300 ${statusFilter === 'unanswered' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 hover:bg-blue-100'}`}>Openstaande casussen</button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-6 flex items-center gap-4">
          <h3 className="text-2xl font-semibold text-blue-700">
            {currentCase ? currentCase.title : ''}
          </h3>
          {currentCases.length > 0 && currentCase && (
            <PogingBadge attemptNumber={attemptNumber} afgerond={!!selfAssessment[currentCase.id]} />
          )}
        </div>
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={goToPreviousCase}
            disabled={currentCaseIndex === 0 || currentCases.length === 0}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentCaseIndex === 0 || currentCases.length === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            ← Vorige
          </button>
          <button
            onClick={goToNextCase}
            disabled={currentCaseIndex === currentCases.length - 1 || currentCases.length === 0}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentCaseIndex === currentCases.length - 1 || currentCases.length === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Volgende →
          </button>
        </div>

        {currentCases.length === 0 && (
          <p className="text-center text-gray-700 mb-4">
            Geen casussen beschikbaar voor het geselecteerde thema.
          </p>
        )}

        {currentCase ? (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="mb-6">
              <div className="mb-4">
                <button
                  className="text-blue-700 font-semibold mb-2 focus:outline-none flex items-center gap-2"
                  onClick={() => setShowCaseDescription(!showCaseDescription)}
                  aria-expanded={showCaseDescription}
                >
                  {showCaseDescription ? '▼' : '►'} Casusbeschrijving
                </button>
                {showCaseDescription && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-700">{currentCase.caseDescription}</p>
                  </div>
                )}
              </div>

              <h4 className="text-lg font-medium text-gray-800 mb-4">
                {currentCase.question}
              </h4>
              
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Jouw analyse:
                </label>
                <textarea
                  value={answers[currentCase.id] || ''}
                  onChange={(e) => onAnswerChange(currentCase.id, e.target.value)}
                  readOnly={!!selfAssessment[currentCase.id]}
                  className={`w-full h-40 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    selfAssessment[currentCase.id] ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="Beschrijf hier je klinische redenering..."
                />
              </div>

              <div className="space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="relative group flex items-center">
                    <button
                      onClick={() => setShowModelAnswer(!showModelAnswer)}
                      disabled={!answers[currentCase.id]}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        !answers[currentCase.id]
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {showModelAnswer ? 'Verberg modelantwoord' : 'Toon modelantwoord'}
                    </button>
                    {!answers[currentCase.id] && (
                      <div className="hidden absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg group-hover:block whitespace-nowrap z-10">
                        Vul eerst je antwoord in om feedback te krijgen
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                          <div className="border-8 border-transparent border-t-gray-800"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="relative group flex items-center">
                    <button
                      onClick={() => checkWithAI(currentCase, answers[currentCase.id] || '')}
                      disabled={!answers[currentCase.id]}
                      className={`px-4 py-2 rounded-lg font-medium ${
                        !answers[currentCase.id]
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      Controleer met AI
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAiFeedbackInfo(v => !v)}
                      className="ml-2 rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-700 w-6 h-6 flex items-center justify-center border border-yellow-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      aria-label="Feedback van AI"
                      style={{ marginTop: 0 }}
                    >
                      <span className="text-sm font-bold">i</span>
                    </button>
                    {showAiFeedbackInfo && (
                      <div
                        ref={aiFeedbackInfoRef}
                        className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-30 bg-white border border-yellow-500 rounded-lg shadow-xl p-3 w-80 text-xs text-gray-800 animate-fadeIn"
                        style={{ minWidth: '320px' }}
                      >
                        <strong className="block mb-1 text-yellow-700 font-semibold">Feedback van AI</strong>
                        <p className="space-y-0.5">
                          AI-feedback is niet statisch en kan elke keer anders zijn. Het is een suggestie die onvolledig, bevooroordeeld of verouderd kan zijn. Gebruik het om nieuwe ideeën op te doen, maar vergelijk het altijd met andere bronnen en perspectieven om tot je eigen weloverwogen conclusie te komen.
                        </p>
                        <span className="block mt-2 text-xs text-gray-500">Klik buiten dit venster of op het i-tje om te sluiten.</span>
                      </div>
                    )}
                  </div>
                </div>

                {showModelAnswer && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">Modelantwoord:</h4>
                    <p className="text-blue-900">{currentCase.modelAnswer}</p>
                    <div className="mt-4">
                      <h4 className="font-medium text-blue-800 mb-2">Beoordelingscriteria:</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {currentCase.criteria.map((criterion, index) => (
                          <li key={index} className="text-blue-900">{criterion}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {aiFeedback && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">AI Analyse:</h4>
                    <pre className="whitespace-pre-wrap text-green-900 font-sans">{aiFeedback}</pre>
                  </div>
                )}

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-gray-700 mb-2">Hoe goed denk je dat je het hebt gedaan?</h4>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-3">
                      {['beginner', 'gevorderd', 'expert'].map((level) => (
                        <div key={level} className="relative group">
                          <button
                            onClick={() => handleSelfAssessment(level)}
                            disabled={!answers[currentCase.id] || selfAssessment[currentCase.id]}
                            className={`px-4 py-2 rounded-lg font-medium capitalize ${
                              !answers[currentCase.id] || selfAssessment[currentCase.id]
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                            } ${
                              selfAssessment[currentCase.id] === level
                                ? level === 'beginner' 
                                  ? 'bg-red-500 hover:bg-red-600 text-white'
                                  : level === 'gevorderd'
                                  ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                  : 'bg-green-500 hover:bg-green-600 text-white'
                                : level === 'beginner'
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : level === 'gevorderd'
                                ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                : 'bg-green-500 hover:bg-green-600 text-white'
                            }`}
                          >
                            {level}
                          </button>
                          {!answers[currentCase.id] && (
                            <div className="hidden absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg group-hover:block whitespace-nowrap z-10">
                              Vul eerst je antwoord in om feedback te krijgen
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                <div className="border-8 border-transparent border-t-gray-800"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {selfAssessment[currentCase.id] && (
                      <div className="flex flex-col gap-2">
                        <div className="bg-green-50 p-3 rounded-lg text-green-700">
                          <p className="font-medium">✓ Antwoord opgeslagen als {selfAssessment[currentCase.id]}</p>
                          {currentVersion[currentCase.id] > 1 && (
                            <p className="text-sm mt-1">Dit is je {currentVersion[currentCase.id]}e poging</p>
                          )}
                        </div>
                        <button
                          onClick={startNewAttempt}
                          className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-medium hover:bg-blue-200"
                        >
                          Opnieuw proberen
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-700 mb-4">
            Geen casussen beschikbaar voor het geselecteerde filter.
          </p>
        )}
        <div className="flex justify-between items-center mt-6">
          <button
            onClick={goToPreviousCase}
            disabled={currentCaseIndex === 0 || currentCases.length === 0}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentCaseIndex === 0 || currentCases.length === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            }`}
          >
            ← Vorige
          </button>
          <button
            onClick={goToNextCase}
            disabled={currentCaseIndex === currentCases.length - 1 || currentCases.length === 0}
            className={`px-4 py-2 rounded-lg font-medium ${
              currentCaseIndex === currentCases.length - 1 || currentCases.length === 0
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            Volgende →
          </button>
        </div>
      </div>
    </div>
  );
}

export default KlinischRedenerenSection; 